import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import SubscriptionFlagPopup from "./SubscriptionFlagPopup";
import { ensureSubscriptionFlag, useSubscriptionFlag } from "./subscriptionFlagStore";

// Screens that must stay reachable while the subscription is expired —
// otherwise the RM could never pay. Prefix match, so sub-routes count too.
const EXPIRED_SAFE_PATHS = [
  "/autopay-mandate",
  "/subscription-billing",
  "/subscription-invoice-preview",
  "/subscription-receipt-preview",
];

const isSafePath = (pathname) =>
  EXPIRED_SAFE_PATHS.some((path) => pathname === path || pathname.startsWith(`${path}/`));

// ================================================================
// Subscription route guard (backend guide S0 + golden rule 7).
//
// Where : mounted ONCE in ProtectedRoute.jsx, so it runs on every
//         protected screen. Renders nothing unless the RM is blocked.
// What  : checks Subscription/subscription-flag on every route change
//         (the first protected page after login covers "on login") and
//         opens SubscriptionFlagPopup when SubscriptionExpired = true.
//         The check goes through ensureSubscriptionFlag, so most route
//         changes reuse the cached answer instead of calling the API —
//         see subscriptionFlagStore.js for how long an answer is reused.
//         The backend blocks nothing itself — this is where the RM panel
//         decides to restrict the service.
// Safety: fail-open. If the flag call fails, no popup is shown, so a
//         backend problem can never lock RMs out. EXPIRED_SAFE_PATHS stay
//         usable while expired so the RM can actually pay.
// ================================================================
const SubscriptionGuard = () => {
  const { pathname } = useLocation();
  const { flag } = useSubscriptionFlag();

  useEffect(() => {
    ensureSubscriptionFlag();
  }, [pathname]);

  // The store outlives a logout (no page reload), so ignore a flag that
  // belongs to the previous user while the fresh one is loading.
  const isOwnFlag =
    !flag?.EmployeeId || String(flag.EmployeeId) === String(localStorage.getItem("ID"));
  const blocked = isOwnFlag && Boolean(flag?.SubscriptionExpired) && !isSafePath(pathname);

  return blocked ? <SubscriptionFlagPopup open flag={flag} /> : null;
};

export default SubscriptionGuard;
